"use client";
import { setAuthentication } from "@/data/redux/appSlice";
import { AppStore, makeStore } from "@/data/redux/store";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { useEffect, useRef } from "react";
import { Provider } from "react-redux";

type Props = {
  auth?: { userId: string; isAuth: boolean; username: string; roles: string[] };
  initActions?: any[];
  children: React.ReactNode;
};

export default function StoreProvider({ children, auth, initActions }: Props) {
  const storeRef = useRef<AppStore>(undefined);
  const queryClient = useRef(undefined);

  if (!queryClient.current) {
    queryClient.current = new QueryClient({
      defaultOptions: {
        queries: {
          refetchOnMount: false
        }
      }
    });

    if (typeof window !== "undefined") {
      (window as any).__TANSTACK_QUERY_CLIENT__ = queryClient.current;
    }
  }

  if (!storeRef.current) {
    // Create the store instance the first time this renders
    storeRef.current = makeStore();
    if (auth) {
      storeRef.current.dispatch(setAuthentication(auth));
    }
    for (const action of initActions ?? []) {
      storeRef.current.dispatch(action);
    }
  }

  // Update auth state whenever the server-provided auth changes
  useEffect(() => {
    if (auth && storeRef.current) {
      storeRef.current.dispatch(setAuthentication(auth));
    }
  }, [auth?.isAuth, auth?.username, auth?.roles?.join(",")]);

  return (
    <QueryClientProvider client={queryClient.current}>
      <Provider store={storeRef.current}>{children}</Provider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}
